import React, { Component, ReactNode } from 'react'
import { AlertTriangle, RefreshCcw, Home } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { logError } from '@/lib/error-handler'

export interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
  resetKeys?: Array<string | number>
  level?: 'app' | 'route' | 'component'
  showResetButton?: boolean
  showHomeButton?: boolean
  isolate?: boolean
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: React.ErrorInfo | null
  errorCount: number
}

/**
 * Base ErrorBoundary component that catches React errors in its children.
 *
 * Features:
 * - Catches rendering errors and lifecycle errors
 * - Displays user-friendly fallback UI
 * - Provides reset functionality
 * - Logs errors to console (and can integrate with Sentry)
 * - Supports different levels (app, route, component) for context-specific handling
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error details
    logError(error, {
      componentStack: errorInfo.componentStack,
      level: this.props.level || 'component',
      errorCount: this.state.errorCount + 1,
    })

    // Update state with error details
    this.setState((prevState) => ({
      errorInfo,
      errorCount: prevState.errorCount + 1,
    }))

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo)
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    // Reset error state if resetKeys change
    if (this.state.hasError && this.props.resetKeys) {
      const prevKeys = prevProps.resetKeys || []
      const currentKeys = this.props.resetKeys

      // Check if any reset key has changed
      const hasResetKeyChanged = currentKeys.some(
        (key, index) => key !== prevKeys[index]
      )

      if (hasResetKeyChanged) {
        this.resetErrorBoundary()
      }
    }
  }

  resetErrorBoundary = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
  }

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default fallback UI based on level
      const { level = 'component', showResetButton = true, showHomeButton = false, isolate = false } = this.props
      const { error, errorInfo, errorCount } = this.state

      // For isolated errors (component level), show minimal error UI
      if (isolate) {
        return (
          <div className="p-4 border border-red-200 bg-red-50 rounded-lg dark:bg-red-950 dark:border-red-800">
            <div className="flex items-center gap-2 text-sm text-red-800 dark:text-red-200">
              <AlertTriangle className="h-4 w-4" />
              <span>An error occurred in this component</span>
              {showResetButton && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={this.resetErrorBoundary}
                  className="ml-auto h-6 px-2 text-xs"
                >
                  <RefreshCcw className="h-3 w-3 mr-1" />
                  Retry
                </Button>
              )}
            </div>
          </div>
        )
      }

      // Full error UI for route and app level errors
      return (
        <div className="flex items-center justify-center min-h-[400px] p-6">
          <Card className="max-w-2xl w-full">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-6 w-6 text-destructive" />
                <CardTitle>
                  {level === 'app' ? 'Application Error' :
                   level === 'route' ? 'Page Error' :
                   'Component Error'}
                </CardTitle>
              </div>
              <CardDescription>
                {level === 'app'
                  ? 'An unexpected error occurred. The application encountered a problem.'
                  : level === 'route'
                  ? 'An error occurred while loading this page.'
                  : 'An error occurred in this component.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Error message */}
              <Alert variant="destructive">
                <AlertTitle>Error Details</AlertTitle>
                <AlertDescription className="mt-2 font-mono text-sm">
                  {error?.message || 'An unknown error occurred'}
                </AlertDescription>
              </Alert>

              {/* Show component stack in development */}
              {import.meta.env.DEV && errorInfo && (
                <Alert>
                  <AlertTitle>Component Stack (Development Only)</AlertTitle>
                  <AlertDescription className="mt-2 font-mono text-xs whitespace-pre-wrap overflow-auto max-h-40">
                    {errorInfo.componentStack}
                  </AlertDescription>
                </Alert>
              )}

              {/* Repeated errors warning */}
              {errorCount > 1 && (
                <Alert>
                  <AlertTitle>Multiple Errors Detected</AlertTitle>
                  <AlertDescription>
                    This error has occurred {errorCount} times. If it persists after resetting, please contact support.
                  </AlertDescription>
                </Alert>
              )}

              {/* Action buttons */}
              <div className="flex gap-2 justify-end">
                {showHomeButton && level !== 'component' && (
                  <Button
                    variant="outline"
                    onClick={() => window.location.href = '/'}
                  >
                    <Home className="mr-2 h-4 w-4" />
                    Go Home
                  </Button>
                )}
                {showResetButton && (
                  <Button onClick={this.resetErrorBoundary}>
                    <RefreshCcw className="mr-2 h-4 w-4" />
                    Try Again
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )
    }

    return this.props.children
  }
}

/**
 * Hook to programmatically reset an error boundary.
 * Use this with a key prop on the ErrorBoundary to force reset.
 */
export function useErrorHandler() {
  const [resetKey, setResetKey] = React.useState(0)

  const reset = React.useCallback(() => {
    setResetKey((prev) => prev + 1)
  }, [])

  return { resetKey, reset }
}
